import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import './shared.css';

interface LaunchInstancePanelProps {
  kgId: string;
  versionId: string;
  onClose: () => void;
  onCreated: () => void;
}

const LaunchInstancePanel: React.FC<LaunchInstancePanelProps> = ({
  kgId,
  versionId,
  onClose,
  onCreated,
}) => {
  const [instanceTypes, setInstanceTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    const loadInstanceTypes = async () => {
      try {
        setLoadingTypes(true);
        const response: any = await apiService.getInstanceTypes();
        if (response.data && Array.isArray(response.data)) {
          setInstanceTypes(response.data);
          if (response.data.length > 0) setSelectedType(response.data[0]);
        }
      } catch {
        setInstanceTypes(['db.t3.micro', 'db.t3.small', 'db.t3.medium']);
        setSelectedType('db.t3.micro');
      } finally {
        setLoadingTypes(false);
      }
    };
    loadInstanceTypes();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedType) {
      setError('Please select an instance type');
      return;
    }
    try {
      setLoading(true);
      await apiService.createInstance(kgId, versionId, selectedType);
      onCreated();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to launch instance'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="side-panel-overlay" onClick={onClose} />
      <div className="side-panel">
        <div className="side-panel-hd">
          <span>Launch Instance</span>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)', fontSize: '18px', lineHeight: 1 }}
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="side-panel-body">
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--muted-foreground)' }}>
              Launching for KG <code className="mono" style={{ fontSize: '12px' }}>{kgId}</code>, version <code className="mono" style={{ fontSize: '12px' }}>v{versionId}</code>
            </p>

            {loadingTypes ? (
              <div className="tab-loader"><span className="tab-spinner" /></div>
            ) : (
              <div className="form-group">
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--foreground)', display: 'block', marginBottom: '6px' }}>
                  Instance Type <span style={{ color: 'var(--destructive)' }}>*</span>
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="form-control"
                >
                  {instanceTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <small style={{ color: 'var(--muted-foreground)', marginTop: '6px', display: 'block', fontSize: '12px' }}>
                  Choose the instance size based on your knowledge graph requirements.
                </small>
              </div>
            )}

            {error && (
              <div className="tab-banner tab-banner--error" style={{ marginTop: '12px' }}>
                <span className="tab-banner-icon">⚠</span>
                <span className="tab-banner-body">{error}</span>
              </div>
            )}
          </div>

          <div className="side-panel-ft">
            <button type="button" className="tab-btn tab-btn--ghost" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="tab-btn tab-btn--primary" disabled={loading || loadingTypes}>
              {loading ? 'Launching…' : 'Launch Instance'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default LaunchInstancePanel;
